import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Grid,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { PreorderEventItem } from '../../../actions/preorderEventActions/PreorderEventActionsTypes';
import { ItemType } from '../../admin/models/ModelTypes';
import { 
  PREORDER_ITEM_UPDATE_SUCCESS,
  PREORDER_EVENTS_FAIL 
} from '../../../actions/preorderEventActions/PreorderEventActionsTypes';

interface EditPriceDialogProps {
  open: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  onUpdate: (updateData: {
    id: number;
    sellPrice: number;
    discountPercentage?: number;
    discountQuantity?: number;
  }) => void;
  item: PreorderEventItem | null;
  isLoading: boolean;
}

const EditPriceDialog: React.FC<EditPriceDialogProps> = ({
  open,
  onClose,
  onUpdate,
  item,
  isLoading
}) => {
  const { t } = useTranslation('global');
  const dispatch = useDispatch();
  
  // Post-process price function (same logic as ItemEdit)
  const postProcessPrice = React.useCallback((sellPrice: number, tax: number = 0) => {
    if (tax === 0) return sellPrice.toFixed(2);
    return ((sellPrice * 100) / (100 + tax)).toFixed(2);
  }, []);

  const [sellPrice, setSellPrice] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState('');
  const [discountQuantity, setDiscountQuantity] = useState('');
  const [loadingItemData, setLoadingItemData] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [fullItemData, setFullItemData] = useState<ItemType | null>(null);
  
  // Track original values for discount calculations
  const [originalDiscountQuantity, setOriginalDiscountQuantity] = useState(0);

  // Calculate the price reactively using fetched data when available
  const calculatedPrice = React.useMemo(() => {
    const sellPriceValue = Number(sellPrice) || 0;
    
    if (fullItemData && fullItemData.priceWithTaxes) {
      return postProcessPrice(sellPriceValue, 13);
    } else {
      // Fallback to standard tax calculation if no data available
      return postProcessPrice(sellPriceValue, 13);
    }
  }, [sellPrice, fullItemData, postProcessPrice]);

  const [errors, setErrors] = useState({
    sellPrice: false,
    discountPercentage: false,
    discountQuantity: false
  });

  // Fetch item data when dialog opens
  const fetchItemData = React.useCallback(async (itemId: number) => {
    try {
      setLoadingItemData(true);
      const apiURL = process.env.REACT_APP_API_URL;
      const token = localStorage.token;
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${apiURL}/seller/getAllItems?id=${itemId}&isPreorder=true`, { headers });
      
      if (response.data && response.data.data && response.data.data.content && response.data.data.content.length > 0) {
        const itemData = response.data.data.content[0];
        setFullItemData(itemData);

        // Initialize form with fetched data
        const initSellPrice = itemData.priceWithTaxes || 0; // Use priceWithTaxes as the sell price
        const initDiscountPercentage = itemData.discount ? itemData.discount.pricePercentage || 0 : 0;
        const initDiscountQuantity = itemData.discount ? (itemData.warehousesQuantities.find((wq: any) => wq.warehouse.isDiscount)?.quantity || 0) : 0;

        setSellPrice(initSellPrice.toString());
        setDiscountPercentage(initDiscountPercentage.toString());
        setDiscountQuantity(initDiscountQuantity.toString());
        setOriginalDiscountQuantity(initDiscountQuantity); // Store original for calculations
      } else {
        // Fallback to default values if fetch fails
        const initSellPrice = 0;
        setSellPrice(initSellPrice.toString());
        setDiscountPercentage('0');
        setDiscountQuantity('0');
      }
    } catch (error) {
      // Fallback initialization
      setSellPrice('0');
      setDiscountPercentage('0');
      setDiscountQuantity('0');
    } finally {
      setLoadingItemData(false);
    }
  }, []);

  useEffect(() => {
    if (open && item) {
      fetchItemData(item.id);
    }
  }, [open, item, fetchItemData]);

  const handleNumericChange = (field: string, value: string) => {
    // Allow empty string, digits, and single decimal point
    const isValidInput = /^$|^\d*\.?\d*$/.test(value);
    
    if (!isValidInput) return;

    // Update the appropriate state
    switch (field) {
      case 'sellPrice':
        setSellPrice(value);
        if (errors.sellPrice) {
          setErrors(prev => ({ ...prev, sellPrice: false }));
        }
        break;
      case 'discountPercentage':
        // Validate percentage range (0-100)
        const percentValue = Number(value);
        if (value === '' || (percentValue >= 0 && percentValue <= 100)) {
          setDiscountPercentage(value);
          if (errors.discountPercentage) {
            setErrors(prev => ({ ...prev, discountPercentage: false }));
          }
        }
        break;
      case 'discountQuantity':
        const quantityValue = Number(value);
        const totalQty = item?.stock || 0; // Use stock from PreorderEventItem
        if (value === '' || (quantityValue >= 0 && quantityValue <= totalQty)) {
          setDiscountQuantity(value);
          if (errors.discountQuantity) {
            setErrors(prev => ({ ...prev, discountQuantity: false }));
          }
        }
        break;
    }
  };

  const validateInputs = () => {
    const newErrors = {
      sellPrice: !sellPrice || isNaN(Number(sellPrice)) || Number(sellPrice) < 0,
      discountPercentage: false,
      discountQuantity: false
    };

    // Validate discount percentage
    if (discountPercentage && (Number(discountPercentage) < 0 || Number(discountPercentage) > 100)) {
      newErrors.discountPercentage = true;
    }

    // Validate discount quantity
    const totalQty = item?.stock || 0; // Use stock from PreorderEventItem
    if (discountQuantity && (Number(discountQuantity) < 0 || Number(discountQuantity) > totalQty)) {
      newErrors.discountQuantity = true;
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = async () => {
    if (!validateInputs() || !item) return;

    setLoadingUpdate(true);

    try {
      const apiURL = process.env.REACT_APP_API_URL;
      const token = localStorage.token;
      const headers = { Authorization: `Bearer ${token}` };

      // First, update the item price
      const payload = {
        id: item.id,
        price: calculatedPrice, // Send the calculated lower/base price
        quantity: 0 // Set to 0 for price updates
      };

      const response = await axios.put(`${apiURL}/admin/updateIndividualItem`, payload, { headers });

      if (response.status === 200) {
        // Handle discount workflow if there are discount values
        const discountPercentageValue = Number(discountPercentage) || 0;
        const discountQuantityValue = Number(discountQuantity) || 0;
        
        if (discountPercentageValue > 0 && discountQuantityValue > 0) {
          // Check if item currently has a discount
          const hasExistingDiscount = fullItemData && (fullItemData as any).discount;
          
          if (!hasExistingDiscount) {
            // No discount associated - register new discount
            const registerPayload = {
              itemId: item.id,
              pricePercentage: discountPercentageValue,
              quantity: discountQuantityValue
            };
            
            await axios.post(`${apiURL}/admin/registerItemDiscount`, registerPayload, { headers });
          } else {
            // Discount is associated - update existing discount
            const quantityDifference = discountQuantityValue - originalDiscountQuantity;
            
            const updatePayload = {
              itemId: item.id,
              pricePercentage: discountPercentageValue,
              quantityToAdd: quantityDifference, // Positive for add, negative for remove
              removeItems: quantityDifference < 0 // true if removing items (negative difference)
            };
            
            await axios.put(`${apiURL}/admin/updateItemDiscount`, updatePayload, { headers });
          }
        }

        // Show success message (green toast)
        dispatch({
          type: PREORDER_ITEM_UPDATE_SUCCESS,
          payload: 'status.success-update'
        });
        
        // Update parent component
        onUpdate({
          id: item.id,
          sellPrice: Number(sellPrice),
          discountPercentage: discountPercentageValue,
          discountQuantity: discountQuantityValue
        });
        
        // Close dialog with refresh to get updated data
        handleClose(true);
      }
    } catch (error) {
      // Show error message
      dispatch({
        type: PREORDER_EVENTS_FAIL,
        payload: 'Error updating price'
      });
    } finally {
      setLoadingUpdate(false);
    }
  };

  const handleClose = (shouldRefresh: boolean = false) => {
    if (!isLoading && !loadingUpdate) {
      onClose(shouldRefresh);
    }
  };

  const handleCancel = () => {
    handleClose(false); // Cancel doesn't refresh
  };

  const handleDialogClose = () => {
    handleClose(false); // Dialog close (ESC, backdrop click) doesn't refresh
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('preorderEventDetails.edit-price')}
      </DialogTitle>
      <DialogContent>
        {loadingItemData ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <CircularProgress />
            <Typography variant="body2" style={{ marginLeft: 16 }}>
              {t('common.loading')}...
            </Typography>
          </div>
        ) : (
          item && (
          <>
            <Typography variant="h6" style={{ marginBottom: 16 }}>
              <strong>{item.name}</strong> ({item.sku})
            </Typography>
            
            <Grid container spacing={3}>
              {/* Sell Price Section */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="initial" style={{ marginBottom: 8, display: 'block' }}>
                  {t('items.sell-price')}:
                </Typography>
                <TextField
                  fullWidth
                  type="text"
                  value={sellPrice}
                  onChange={(e) => handleNumericChange('sellPrice', e.target.value)}
                  error={errors.sellPrice}
                  helperText={errors.sellPrice ? t('common.field-required') : ''}
                  disabled={isLoading || loadingUpdate}
                  onKeyDown={(evt) => evt.key.toLowerCase() === 'e' && evt.preventDefault()}
                />
              </Grid>

              {/* Price Section (calculated, disabled) */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="initial" style={{ marginBottom: 8, display: 'block' }}>
                  Precio:
                </Typography>
                <TextField
                  fullWidth
                  type="text"
                  value={calculatedPrice}
                  disabled
                  onKeyDown={(evt) => evt.key.toLowerCase() === 'e' && evt.preventDefault()}
                />
              </Grid>

              {/* Discount Section */}
              <Grid item xs={12}>
                <Typography variant="caption" color="initial" style={{ marginBottom: 8, display: 'block' }}>
                  {t('items.discounts')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label={t('items.percentage')}
                      type="text"
                      value={discountPercentage}
                      onChange={(e) => handleNumericChange('discountPercentage', e.target.value)}
                      error={errors.discountPercentage}
                      helperText={errors.discountPercentage ? 'Invalid percentage (0-100)' : ''}
                      disabled={isLoading || loadingUpdate || (item?.stock || 0) < 1}
                      onKeyDown={(evt) => evt.key.toLowerCase() === 'e' && evt.preventDefault()}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label={t('items.quantity')}
                      type="text"
                      value={discountQuantity}
                      onChange={(e) => handleNumericChange('discountQuantity', e.target.value)}
                      error={errors.discountQuantity}
                      helperText={errors.discountQuantity ? 'Cannot exceed total quantity' : ''}
                      disabled={isLoading || loadingUpdate || (item?.stock || 0) < 1}
                      onKeyDown={(evt) => evt.key.toLowerCase() === 'e' && evt.preventDefault()}
                    />
                  </Grid>
                </Grid>
                {(item?.stock || 0) < 1 && (
                  <Typography variant="caption" color="error" style={{ marginTop: 8, display: 'block' }}>
                    {t('items.error-discount-no-stock')}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={isLoading || loadingUpdate}>
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          disabled={isLoading || loadingUpdate}
        >
          {(isLoading || loadingUpdate) ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 8 }} />
              {t('preorderEventDetails.updating')}
            </>
          ) : (
            t('preorderEventDetails.update')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPriceDialog;